import { useEffect } from 'react'
import Lenis from 'lenis'

// Shared instance so overlays (e.g. the fullscreen showreel) can pause page scrolling
let instance: Lenis | null = null

/** Pause / resume smooth scrolling (no-op when smooth scrolling is disabled). */
export function setSmoothScrollPaused(paused: boolean) {
  if (!instance) return
  if (paused) instance.stop()
  else instance.start()
}

/**
 * Very subtle inertial smooth scrolling across the page.
 * Disabled for users who prefer reduced motion. In-page anchor links glide too.
 */
export function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const lenis = new Lenis({
      lerp: 0.2, // minimal inertia — barely-there smoothing
      wheelMultiplier: 1,
      smoothWheel: true,
    })
    instance = lenis

    let raf = 0
    const loop = (time: number) => {
      lenis.raf(time)
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)

    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a[href^="#"]') as HTMLAnchorElement | null
      if (!link) return
      const hash = link.getAttribute('href')
      if (!hash || hash === '#') return
      const target = document.querySelector(hash)
      if (!target) return
      e.preventDefault()
      lenis.scrollTo(target as HTMLElement, { offset: hash === '#top' ? 0 : -72, duration: 0.8 })
    }
    document.addEventListener('click', onClick)

    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('click', onClick)
      lenis.destroy()
      if (instance === lenis) instance = null
    }
  }, [])
}
